import { useEffect } from "react";
import clsx from "clsx";
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from "lucide-react";
import { Icon } from "@cntrl/ui";
import { useToasts, type ToastTone } from "./toasts.js";
import styles from "./ToastHost.module.scss";

/** How long a toast lingers before auto-dismiss. The two a reader has to ACT on — an error and a warning —
 *  persist longer, because both carry a sentence rather than a confirmation. */
const AUTO_DISMISS_MS: Record<ToastTone, number> = { error: 7000, warning: 7000, success: 4000, info: 5000 };

/** The tone glyph. Colour alone must not be the thing that says which tone this is: the message text
 *  already carries the meaning, and the icon is the second, non-chromatic carrier (WCAG 1.4.1). */
const TONE_ICON = { error: AlertCircle, warning: TriangleAlert, success: CheckCircle2, info: Info } as const;

/** A warning is as assertive as an error — both interrupt because something needs the reader's attention;
 *  a success or an info is a confirmation and stays polite. */
const ASSERTIVE: Record<ToastTone, boolean> = { error: true, warning: true, success: false, info: false };

/**
 * Renders the transient toast stack. Mounted once inside the authed shell layout. Errors and warnings are
 * `role="alert"` (assertive) so the rollback of a failed optimistic write is announced; success/info are
 * polite. Each toast auto-dismisses and is manually closeable.
 */
export function ToastHost() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);
  if (toasts.length === 0) return null;
  return (
    <div className={styles.host} aria-label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} id={t.id} message={t.message} tone={t.tone} onDismiss={dismiss} />
      ))}
    </div>
  );
}

function ToastItem({
  id,
  message,
  tone,
  onDismiss,
}: {
  id: string;
  message: string;
  tone: ToastTone;
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), AUTO_DISMISS_MS[tone]);
    return () => clearTimeout(timer);
  }, [id, tone, onDismiss]);

  return (
    <div className={clsx(styles.toast, styles[tone])} role={ASSERTIVE[tone] ? "alert" : "status"}>
      <Icon icon={TONE_ICON[tone]} size={20} className={styles.icon} />
      <span className={styles.message}>{message}</span>
      <button type="button" className={styles.close} aria-label="Dismiss" onClick={() => onDismiss(id)}>
        &times;
      </button>
    </div>
  );
}
